import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PaymentMethodsEdit from "./PaymentMethodEdit";
import PaymentHistory from "./PaymentHistory";
import DeveloperAccess from "./DeveloperAccess";
import Credit from "./Credit";
import UsageBreakdown from "./UsageBreakdown";
import config from "../Config";
import { getCookieToken, decodeJwtPayload } from "../functions/authSession";
import "./pages.css";

const NAV_GROUPS = [
  {
    title: "Account",
    items: [{ id: "settings", label: "Settings" }],
  },
  {
    title: "Billing",
    items: [
      { id: "usage",   label: "Usage" },
      { id: "payment", label: "Payment methods" },
      { id: "history", label: "History" },
      { id: "credit",  label: "Add credit" },
    ],
  },
  {
    title: "Developer",
    items: [
      { id: "developer", label: "Developer Access" },
      { id: "api",       label: "API docs" },
    ],
  },
];

const TAB_IDS = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id));

/** The session cookie holds the Cognito id token, whose payload carries the login email. */
function loginEmail() {
  const payload = decodeJwtPayload(getCookieToken());
  return payload?.email || payload?.["cognito:username"] || "";
}

const Account = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const initialTab = TAB_IDS.includes(requestedTab) ? requestedTab : "settings";
  const [active, setActive] = useState(initialTab);
  const email = loginEmail();

  const selectTab = (id) => {
    setActive(id);
    navigate(`/account?tab=${id}`, { replace: true });
  };

  return (
    <div className="acct-page">

      {/* ── Top banner ── */}
      <div className="acct-banner">
        <div className="acct-avatar">{(email[0] || "M").toUpperCase()}</div>
        <div>
          <h1 className="acct-title">My Account</h1>
          <p className="acct-subtitle">{email || "Manage your settings and billing"}</p>
        </div>
      </div>

      {/* ── Sidebar + content ── */}
      <div className="acct-layout">
        <nav className="acct-sidebar">
          {NAV_GROUPS.map((group) => (
            <div className="acct-nav-group" key={group.title}>
              <div className="acct-nav-group-title">{group.title}</div>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  className={`acct-nav-item ${active === item.id ? "active" : ""}`}
                  onClick={() => selectTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="acct-content">
          {active === "settings" && (
            <section className="acct-card">
              <div className="acct-card-header">
                <h2>Settings</h2>
              </div>
              <div className="acct-card-body">
                <div className="acct-row">
                  <div className="acct-row-label">Email</div>
                  <div className="acct-row-value acct-row-value--text">{email || "—"}</div>
                </div>
                <div className="acct-row">
                  <div className="acct-row-label">Password</div>
                  <div className="acct-row-value">••••••••••••</div>
                  <Link to="/resetpassword" className="acct-action-link">Change</Link>
                </div>
              </div>
            </section>
          )}

          {active === "payment" && <PaymentMethodsEdit />}
          {active === "history" && <PaymentHistory />}
          {active === "usage"   && <UsageBreakdown />}
          {active === "credit"  && <Credit />}
          {active === "developer" && <DeveloperAccess />}

          {active === "api" && (
            <section className="acct-card">
              <div className="acct-card-header">
                <h2>API docs</h2>
              </div>
              <div className="acct-card-body">
                {config.RUNNER_API ? (
                  <div className="acct-api-link-row">
                    <p>Access the interactive Swagger UI to call API endpoints directly using your token or API key:</p>
                    <a
                      className="acct-api-link"
                      href={`${config.RUNNER_API}/docs`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {config.RUNNER_API}/docs
                    </a>
                  </div>
                ) : (
                  <p className="acct-api-no-url">No Runner API URL configured. Set it on the login page first.</p>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

    </div>
  );
};

export default Account;
